import React, { useState, useEffect } from 'react';
import {
  FileText,
  Building2,
  Calendar,
  Search,
  RefreshCw,
  Download,
  Grid3x3,
  List,
  User,
  Clock,
} from 'lucide-react';
import { establishmentAPI, formsAPI, employeeAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import './UpdateEmployee.css';

const ViewForms = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  // State management
  const [forms, setForms] = useState([]);
  const [allForms, setAllForms] = useState([]); // Unfiltered forms for stats
  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [activeCard, setActiveCard] = useState(null); // Track active filter card

  // Filters
  const [filters, setFilters] = useState({
    establishment_id: lockedEstId || '',
    form_type: '',
    month: '',
    year: new Date().getFullYear(),
    search: '',
  });
  const [searchFocused, setSearchFocused] = useState(false);

  // PDF loading state
  const [pdfLoading, setPdfLoading] = useState(null);

  // Pagination
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(0);

  // Employee/Individual Forms - Generated per employee
  const employeeForms = [
    { value: 'form_19', label: 'Form 19 - Payslip' },
    { value: 'form_f', label: 'Form F - Leave Register' },
    { value: 'form_q', label: 'Form Q - Appointment Order' },
    { value: 'form_X', label: 'Form X - Wages Register' },
    { value: 'form_xiv', label: 'Form XIV - Employment Card' },
  ];

  // Establishment/Consolidated Forms - One form for all employees
  const establishmentForms = [
    { value: 'form_a', label: 'Form A - Employee Register' },
    { value: 'form_s', label: 'Form S - Notice of Daily Hours' },
    { value: 'form_t', label: 'Form T - Muster Roll cum Wages' },
    { value: 'form_u', label: 'Form U - Annual Return' },
    { value: 'form_v', label: 'Form V - Festival Holidays' },
    { value: 'form_XIII', label: 'Form XIII - Minimum Wages' },
    { value: 'form_XXIII', label: 'Form XXIII - Wages Register' },
    { value: 'form_XXIX', label: 'Form XXIX - Advances Register' },
  ];

  // Compliance & Statement Forms
  const statementForms = [
    { value: 'bank_statement', label: 'Bank Statement' },
    { value: 'epf_esi_statement', label: 'EPF ESI Statement' },
    { value: 'esi_statement', label: 'ESI Statement' },
  ];

  // Combine all for easy lookup
  const allFormTypes = [
    ...employeeForms,
    ...establishmentForms,
    ...statementForms
  ];

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setFilters(prev => ({ ...prev, establishment_id: lockedEstId }));
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial data fetch
  useEffect(() => {
    fetchEstablishments();
    fetchEmployees();
  }, []);

  // Fetch all forms for stats when establishment changes
  useEffect(() => {
    if (filters.establishment_id) {
      fetchAllForms();
    }
  }, [filters.establishment_id]);

  // Fetch forms when filters or active card change
  useEffect(() => {
    if (filters.establishment_id) {
      fetchForms();
    }
  }, [filters.establishment_id, filters.form_type, filters.month, filters.year, filters.search, activeCard]);

  const fetchEstablishments = async () => {
    if (lockedEstId) { setLoading(false); return; } // ClientAdmin: already set
    try {
      const response = await establishmentAPI.getAll();
      const estList = response.establishments || [];
      setEstablishments(estList);

      // Auto-select first establishment if available
      if (estList.length > 0 && !filters.establishment_id) {
        setFilters(prev => ({ ...prev, establishment_id: estList[0].id }));
      }
    } catch (error) {
      console.error('Error fetching establishments:', error);
      setError('Error loading establishments');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await employeeAPI.getAll();
      setEmployees(response.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchAllForms = async () => {
    try {
      // Fetch all forms for the selected establishment (no other filters) for stats
      const params = {};
      if (filters.establishment_id) params.establishment_id = filters.establishment_id;

      const response = await formsAPI.listGeneratedForms(params);
      let formsList = response.forms || [];

      // Deduplicate
      const latestForms = {};
      formsList.forEach(form => {
        const key = `${form.form_type || ''}_${form.establishment_id || ''}_${form.employee_id || ''}_${form.month || ''}_${form.year || ''}`;
        if (!latestForms[key] || new Date(form.generated_at) > new Date(latestForms[key].generated_at)) {
          latestForms[key] = form;
        }
      });

      setAllForms(Object.values(latestForms));
    } catch (error) {
      console.error('Error fetching all forms:', error);
    }
  };

  const fetchForms = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.establishment_id) params.establishment_id = filters.establishment_id;
      if (filters.form_type) params.form_type = filters.form_type;
      if (filters.month) params.month = filters.month;
      if (filters.year) params.year = filters.year;

      const response = await formsAPI.listGeneratedForms(params);
      let formsList = response.forms || [];

      // Apply category filter based on active card
      if (activeCard === 'employee_forms') {
        const employeeFormTypes = employeeForms.map(f => f.value.toLowerCase());
        formsList = formsList.filter(form =>
          employeeFormTypes.includes(form.form_type?.toLowerCase()) || form.employee_id
        );
      } else if (activeCard === 'establishment_forms') {
        const establishmentFormTypes = establishmentForms.map(f => f.value.toLowerCase());
        formsList = formsList.filter(form =>
          establishmentFormTypes.includes(form.form_type?.toLowerCase()) && !form.employee_id
        );
      } else if (activeCard === 'statements') {
        const statementFormTypes = statementForms.map(f => f.value.toLowerCase());
        formsList = formsList.filter(form =>
          statementFormTypes.includes(form.form_type?.toLowerCase())
        );
      }

      // Apply search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        formsList = formsList.filter(
          (form) =>
            form.form_type?.toLowerCase().includes(searchLower) ||
            form.file_name?.toLowerCase().includes(searchLower) ||
            form.establishment_name?.toLowerCase().includes(searchLower) ||
            form.employee_id?.toString().toLowerCase().includes(searchLower)
        );
      }

      // Keep only the latest version of each form
      const latestForms = {};
      formsList.forEach(form => {
        const key = `${form.form_type || ''}_${form.establishment_id || ''}_${form.employee_id || ''}_${form.month || ''}_${form.year || ''}`;
        if (!latestForms[key] || new Date(form.generated_at) > new Date(latestForms[key].generated_at)) {
          latestForms[key] = form;
        }
      });

      setForms(Object.values(latestForms));
      setPage(0);
    } catch (error) {
      console.error('Error fetching forms:', error);
      setError('Error loading forms: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  const handleDownload = async (form) => {
    try {
      setPdfLoading(form.id);
      const pdfUrl = form.pdf_url || form.s3_url;
      if (pdfUrl) {
        window.open(pdfUrl, '_blank');
      } else {
        setError('PDF file not available for this form');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Error downloading PDF: ' + error.message);
    } finally {
      setPdfLoading(null);
    }
  };

  const handleRefresh = () => {
    fetchAllForms();
    fetchForms();
  };

  // Stat calculations
  const getTotalFormsCount = () => allForms.length;

  const getThisMonthCount = () => {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    return allForms.filter(form =>
      parseInt(form.month) === currentMonth &&
      parseInt(form.year) === currentYear
    ).length;
  };

  const getLast7DaysCount = () => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return allForms.filter(form =>
      form.generated_at && new Date(form.generated_at) >= sevenDaysAgo
    ).length;
  };

  const getThisYearCount = () => {
    const currentYear = new Date().getFullYear();
    return allForms.filter(form =>
      parseInt(form.year) === currentYear
    ).length;
  };

  const getEmployeeFormsCount = () => {
    const employeeFormTypes = employeeForms.map(f => f.value.toLowerCase());
    return allForms.filter(form =>
      employeeFormTypes.includes(form.form_type?.toLowerCase()) || form.employee_id
    ).length;
  };

  const getOrganizationFormsCount = () => {
    const establishmentFormTypes = establishmentForms.map(f => f.value.toLowerCase());
    return allForms.filter(form =>
      establishmentFormTypes.includes(form.form_type?.toLowerCase()) && !form.employee_id
    ).length;
  };

  const getStatementsCount = () => {
    const statementFormTypes = statementForms.map(f => f.value.toLowerCase());
    return allForms.filter(form =>
      statementFormTypes.includes(form.form_type?.toLowerCase())
    ).length;
  };

  // Card click handlers
  const handleCardClick = (cardType) => {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    if (activeCard === cardType) {
      // Deactivate - reset filters
      setActiveCard(null);
      setFilters(prev => ({
        ...prev,
        month: '',
        year: currentYear,
        form_type: ''
      }));
    } else {
      // Activate card filter
      setActiveCard(cardType);

      switch(cardType) {
        case 'this_month':
          setFilters(prev => ({
            ...prev,
            month: currentMonth,
            year: currentYear,
            form_type: ''
          }));
          break;
        case 'last_7_days':
          setFilters(prev => ({
            ...prev,
            month: currentMonth,
            year: currentYear,
            form_type: ''
          }));
          break;
        case 'this_year':
          setFilters(prev => ({
            ...prev,
            month: '',
            year: currentYear,
            form_type: ''
          }));
          break;
        case 'employee_forms':
        case 'establishment_forms':
        case 'statements':
          // These are handled by the form type filtering in fetchForms
          // Don't set specific filters, just trigger re-render
          setFilters(prev => ({ ...prev }));
          break;
        default:
          break;
      }
    }
  };

  const getFormTypeLabel = (formType) => {
    if (!formType) return 'Unknown';
    const formConfig = allFormTypes.find(f => f.value.toLowerCase() === formType.toLowerCase());
    return formConfig ? formConfig.label : formType;
  };

  const getEmployeeName = (form) => {
    // Use employee_name from API if available
    if (form.employee_name) {
      return form.employee_name;
    }
    // Fallback to lookup by ID
    if (form.employee_id) {
      const employee = employees.find(e => e.id === form.employee_id);
      return employee ? `${employee.first_name} ${employee.last_name}` : form.employee_id;
    }
    return 'All Employees';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Grid View
  const renderGridView = () => {
    const totalPages = Math.ceil(forms.length / PAGE_SIZE);
    const paged = forms.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    return (
    <div>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '1.5rem',
      marginBottom: '1rem',
    }}>
      {paged.map((form) => (
        <div
          key={form.id}
          style={{
            background: 'var(--theme-surface)',
            borderRadius: '0.75rem',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            transition: 'all 0.2s',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '1.25rem',
            color: 'white',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '0.5rem'
            }}>
              <FileText size={20} />
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '700',
                margin: 0,
              }}>
                {getFormTypeLabel(form.form_type)}
              </h3>
            </div>
            <p style={{
              fontSize: '0.75rem',
              margin: 0,
              opacity: 0.9,
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
            }}>
              <Building2 size={12} />
              {form.establishment_name || 'N/A'}
            </p>
          </div>

          {/* Content */}
          <div style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <Calendar size={14} />
                  Period
                </span>
                <span style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#374151' }}>
                  {form.month ? monthNames[form.month - 1] : 'All'} {form.year || ''}
                </span>
              </div>

              {form.employee_id && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <User size={14} />
                    Employee
                  </span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#374151' }}>
                    {getEmployeeName(form)}
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <Clock size={14} />
                  Generated
                </span>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {formatDate(form.generated_at)}
                </span>
              </div>
            </div>

            {/* Download button */}
            <button
              onClick={() => handleDownload(form)}
              disabled={pdfLoading === form.id}
              style={{
                width: '100%',
                padding: '0.625rem',
                background: 'transparent',
                color: '#4f46e5',
                border: '1.5px solid #667eea',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#667eea'; e.currentTarget.style.color = 'white'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#4f46e5'; }}
            >
              <Download size={16} />
              {pdfLoading === form.id ? 'Downloading...' : 'Download PDF'}
            </button>
          </div>
        </div>
      ))}
    </div>
    {/* Pagination */}
    {totalPages > 1 && (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0' }}>
        <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
          Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, forms.length)} of {forms.length} forms
        </span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: '0.5rem 1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', background: page === 0 ? '#f1f3f4' : '#ffffff', color: page === 0 ? '#9ca3af' : '#374151', cursor: page === 0 ? 'default' : 'pointer', fontWeight: '600', fontSize: '0.8125rem' }}>← Previous</button>
          <span style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem', color: '#374151', fontWeight: '600' }}>Page {page + 1} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ padding: '0.5rem 1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', background: page >= totalPages - 1 ? '#f1f3f4' : '#ffffff', color: page >= totalPages - 1 ? '#9ca3af' : '#374151', cursor: page >= totalPages - 1 ? 'default' : 'pointer', fontWeight: '600', fontSize: '0.8125rem' }}>Next →</button>
        </div>
      </div>
    )}
    </div>
  );
  };

  // List View
  const renderListView = () => {
    const totalPages = Math.ceil(forms.length / PAGE_SIZE);
    const paged = forms.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    return (
    <div>
    <div style={{ background: 'var(--theme-surface)', borderRadius: '0.75rem', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '1rem' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--theme-surfaceHover)', borderBottom: '2px solid #e5e7eb' }}>
            <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Form Type</th>
            <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Organization</th>
            <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Period</th>
            <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Employee</th>
            <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Generated</th>
            <th style={{ padding: '0.875rem 1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paged.map((form, idx) => (
            <tr
              key={form.id}
              style={{
                borderBottom: '1px solid #f8f9fa',
                background: idx % 2 === 0 ? '#ffffff' : '#f1f3f4'
              }}
            >
              <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#111827', fontWeight: '600' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FileText size={16} style={{ color: '#667eea' }} />
                  {getFormTypeLabel(form.form_type)}
                </div>
              </td>
              <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                {form.establishment_name || 'N/A'}
              </td>
              <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                {form.month ? monthNames[form.month - 1] : 'All'} {form.year}
              </td>
              <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                {getEmployeeName(form)}
              </td>
              <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                {formatDate(form.generated_at)}
              </td>
              <td style={{ padding: '1rem', textAlign: 'center' }}>
                <button
                  onClick={() => handleDownload(form)}
                  disabled={pdfLoading === form.id}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'transparent',
                    color: '#4f46e5',
                    border: '1.5px solid #667eea',
                    borderRadius: '0.375rem',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#667eea'; e.currentTarget.style.color = 'white'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#4f46e5'; }}
                >
                  <Download size={14} />
                  {pdfLoading === form.id ? 'Loading...' : 'Download'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {forms.length === 0 && (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
          <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
          <p style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>No forms found</p>
          <p style={{ fontSize: '0.875rem' }}>Try adjusting your filters or generate some forms</p>
        </div>
      )}
    </div>
    {/* Pagination */}
    {totalPages > 1 && (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0' }}>
        <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
          Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, forms.length)} of {forms.length} forms
        </span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{ padding: '0.5rem 1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', background: page === 0 ? '#f1f3f4' : '#ffffff', color: page === 0 ? '#9ca3af' : '#374151', cursor: page === 0 ? 'default' : 'pointer', fontWeight: '600', fontSize: '0.8125rem' }}
          >
            ← Previous
          </button>
          <span style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem', color: '#374151', fontWeight: '600' }}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            style={{ padding: '0.5rem 1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', background: page >= totalPages - 1 ? '#f1f3f4' : '#ffffff', color: page >= totalPages - 1 ? '#9ca3af' : '#374151', cursor: page >= totalPages - 1 ? 'default' : 'pointer', fontWeight: '600', fontSize: '0.8125rem' }}
          >
            Next →
          </button>
        </div>
      </div>
    )}
    </div>
  );
  };

  return (
    <div style={{ padding: '2rem', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: '1.875rem',
          fontWeight: '800',
          color: '#111827',
          marginBottom: '0.5rem',
        }}>
          View Forms
        </h1>
        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          View and download generated forms
        </p>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1.5rem',
          color: '#991b1b',
        }}>
          {error}
        </div>
      )}

      {/* Filters Section */}
      <div style={{
        background: 'var(--theme-surface)',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        marginBottom: '2rem',
        border: '1px solid #e5e7eb',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem',
        }}>
          {/* Organization — hidden for ClientAdmin */}
          {!lockedEstId && (
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.75rem',
              fontWeight: '700',
              color: '#374151',
              marginBottom: '0.5rem',
              textTransform: 'uppercase',
            }}>
              Establishment
            </label>
            <select
              name="establishment_id"
              value={filters.establishment_id}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '0.625rem 0.875rem',
                border: '1.5px solid #e5e7eb',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: '#111827',
                background: 'var(--theme-surfaceHover)',
                cursor: 'pointer',
              }}
            >
              <option value="">Select Organization</option>
              {establishments.map((est) => (
                <option key={est.id} value={est.id}>
                  {est.name}
                </option>
              ))}
            </select>
          </div>
          )}

          {/* Form Type */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.75rem',
              fontWeight: '700',
              color: '#374151',
              marginBottom: '0.5rem',
              textTransform: 'uppercase',
            }}>
              Form Type
            </label>
            <select
              name="form_type"
              value={filters.form_type}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '0.625rem 0.875rem',
                border: '1.5px solid #e5e7eb',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: '#111827',
                background: 'var(--theme-surfaceHover)',
                cursor: 'pointer',
              }}
            >
              <option value="">All Forms</option>
              <optgroup label="📄 Employee Forms (Individual)">
                {employeeForms.map((form) => (
                  <option key={form.value} value={form.value}>
                    {form.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="🏢 Organization Forms (Consolidated)">
                {establishmentForms.map((form) => (
                  <option key={form.value} value={form.value}>
                    {form.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="📊 Compliance & Statements">
                {statementForms.map((form) => (
                  <option key={form.value} value={form.value}>
                    {form.label}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>

          {/* Month */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.75rem',
              fontWeight: '700',
              color: '#374151',
              marginBottom: '0.5rem',
              textTransform: 'uppercase',
            }}>
              Month
            </label>
            <select
              name="month"
              value={filters.month}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '0.625rem 0.875rem',
                border: '1.5px solid #e5e7eb',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: '#111827',
                background: 'var(--theme-surfaceHover)',
                cursor: 'pointer',
              }}
            >
              <option value="">All Months</option>
              {monthNames.map((month, idx) => (
                <option key={idx + 1} value={idx + 1}>
                  {month}
                </option>
              ))}
            </select>
          </div>

          {/* Year */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.75rem',
              fontWeight: '700',
              color: '#374151',
              marginBottom: '0.5rem',
              textTransform: 'uppercase',
            }}>
              Year
            </label>
            <select
              name="year"
              value={filters.year}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '0.625rem 0.875rem',
                border: '1.5px solid #e5e7eb',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: '#111827',
                background: 'var(--theme-surfaceHover)',
                cursor: 'pointer',
              }}
            >
              <option value="">All Years</option>
              {[2026, 2025, 2024, 2023, 2022].map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search Bar */}
        <div style={{ position: 'relative' }}>
          {!searchFocused && !filters.search && (
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
                pointerEvents: 'none',
              }}
            />
          )}
          <input
            type="text"
            placeholder="Search by form type, file name, or organization..."
            value={filters.search}
            onChange={handleSearch}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: '1.5px solid #e5e7eb',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              color: '#111827',
              background: 'var(--theme-surfaceHover)',
            }}
          />
        </div>
      </div>

      {/* Stat Cards */}
      {filters.establishment_id && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}>
          {/* Total Forms */}
          <div
            onClick={() => {
              if (activeCard) {
                setActiveCard(null);
                setFilters(prev => ({ ...prev, month: '', year: new Date().getFullYear() }));
              }
            }}
            style={{
              background: activeCard === null
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #e0e7ff 0%, #ede9fe 100%)',
              color: activeCard === null ? 'white' : '#6366f1',
              padding: '1.25rem',
              borderRadius: '0.75rem',
              border: 'none',
              boxShadow: activeCard === null
                ? '0 4px 6px rgba(102, 126, 234, 0.25)'
                : '0 2px 4px rgba(99, 102, 241, 0.15)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = activeCard === null
                ? '0 6px 12px rgba(102, 126, 234, 0.35)'
                : '0 4px 8px rgba(99, 102, 241, 0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = activeCard === null
                ? '0 4px 6px rgba(102, 126, 234, 0.25)'
                : '0 2px 4px rgba(99, 102, 241, 0.15)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', opacity: activeCard === null ? 0.9 : 0.6, fontWeight: '600', textTransform: 'uppercase' }}>
                Total Forms
              </div>
              <FileText size={20} style={{ opacity: activeCard === null ? 0.8 : 0.6 }} />
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '800', lineHeight: '1' }}>
              {getTotalFormsCount()}
            </div>
            <div style={{ fontSize: '0.7rem', opacity: activeCard === null ? 0.8 : 0.5, marginTop: '0.375rem' }}>
              All time
            </div>
          </div>

          {/* This Month */}
          <div
            onClick={() => handleCardClick('this_month')}
            style={{
              background: activeCard === 'this_month' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'var(--theme-surface, #ffffff)',
              color: activeCard === 'this_month' ? 'white' : 'var(--theme-text, #111827)',
              padding: '1.25rem',
              borderRadius: '0.75rem',
              border: activeCard === 'this_month' ? 'none' : '1px solid var(--theme-border, #e5e7eb)',
              boxShadow: activeCard === 'this_month' ? '0 4px 6px rgba(16, 185, 129, 0.25)' : '0 1px 3px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = activeCard === 'this_month'
                ? '0 6px 12px rgba(16, 185, 129, 0.35)'
                : '0 4px 6px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = activeCard === 'this_month'
                ? '0 4px 6px rgba(16, 185, 129, 0.25)'
                : '0 1px 3px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', opacity: activeCard === 'this_month' ? 0.9 : 0.6, fontWeight: '600', textTransform: 'uppercase' }}>
                This Month
              </div>
              <Calendar size={20} style={{ opacity: activeCard === 'this_month' ? 0.8 : 0.6 }} />
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '800', lineHeight: '1' }}>
              {getThisMonthCount()}
            </div>
            <div style={{ fontSize: '0.7rem', opacity: activeCard === 'this_month' ? 0.8 : 0.5, marginTop: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {monthNames[new Date().getMonth()]} {new Date().getFullYear()}
              {activeCard === 'this_month' && (
                <span style={{
                  background: 'rgba(255,255,255,0.3)',
                  padding: '0.125rem 0.375rem',
                  borderRadius: '1rem',
                  fontSize: '0.625rem',
                  fontWeight: '600'
                }}>
                  ACTIVE
                </span>
              )}
            </div>
          </div>

          {/* Last 7 Days */}
          <div
            onClick={() => handleCardClick('last_7_days')}
            style={{
              background: activeCard === 'last_7_days' ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'var(--theme-surface, #ffffff)',
              color: activeCard === 'last_7_days' ? 'white' : 'var(--theme-text, #111827)',
              padding: '1.25rem',
              borderRadius: '0.75rem',
              border: activeCard === 'last_7_days' ? 'none' : '1px solid var(--theme-border, #e5e7eb)',
              boxShadow: activeCard === 'last_7_days' ? '0 4px 6px rgba(245, 158, 11, 0.25)' : '0 1px 3px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = activeCard === 'last_7_days'
                ? '0 6px 12px rgba(245, 158, 11, 0.35)'
                : '0 4px 6px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = activeCard === 'last_7_days'
                ? '0 4px 6px rgba(245, 158, 11, 0.25)'
                : '0 1px 3px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', opacity: activeCard === 'last_7_days' ? 0.9 : 0.6, fontWeight: '600', textTransform: 'uppercase' }}>
                Recent
              </div>
              <Clock size={20} style={{ opacity: activeCard === 'last_7_days' ? 0.8 : 0.6 }} />
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '800', lineHeight: '1' }}>
              {getLast7DaysCount()}
            </div>
            <div style={{ fontSize: '0.7rem', opacity: activeCard === 'last_7_days' ? 0.8 : 0.5, marginTop: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Last 7 days
              {activeCard === 'last_7_days' && (
                <span style={{
                  background: 'rgba(255,255,255,0.3)',
                  padding: '0.125rem 0.375rem',
                  borderRadius: '1rem',
                  fontSize: '0.625rem',
                  fontWeight: '600'
                }}>
                  ACTIVE
                </span>
              )}
            </div>
          </div>

          {/* This Year */}
          <div
            onClick={() => handleCardClick('this_year')}
            style={{
              background: activeCard === 'this_year' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'var(--theme-surface, #ffffff)',
              color: activeCard === 'this_year' ? 'white' : 'var(--theme-text, #111827)',
              padding: '1.25rem',
              borderRadius: '0.75rem',
              border: activeCard === 'this_year' ? 'none' : '1px solid var(--theme-border, #e5e7eb)',
              boxShadow: activeCard === 'this_year' ? '0 4px 6px rgba(59, 130, 246, 0.25)' : '0 1px 3px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = activeCard === 'this_year'
                ? '0 6px 12px rgba(59, 130, 246, 0.35)'
                : '0 4px 6px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = activeCard === 'this_year'
                ? '0 4px 6px rgba(59, 130, 246, 0.25)'
                : '0 1px 3px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', opacity: activeCard === 'this_year' ? 0.9 : 0.6, fontWeight: '600', textTransform: 'uppercase' }}>
                This Year
              </div>
              <Calendar size={20} style={{ opacity: activeCard === 'this_year' ? 0.8 : 0.6 }} />
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '800', lineHeight: '1' }}>
              {getThisYearCount()}
            </div>
            <div style={{ fontSize: '0.7rem', opacity: activeCard === 'this_year' ? 0.8 : 0.5, marginTop: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {new Date().getFullYear()}
              {activeCard === 'this_year' && (
                <span style={{
                  background: 'rgba(255,255,255,0.3)',
                  padding: '0.125rem 0.375rem',
                  borderRadius: '1rem',
                  fontSize: '0.625rem',
                  fontWeight: '600'
                }}>
                  ACTIVE
                </span>
              )}
            </div>
          </div>

          {/* Employee Forms Card */}
          <div
            onClick={() => handleCardClick('employee_forms')}
            style={{
              background: activeCard === 'employee_forms' ? 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' : 'var(--theme-surface, #ffffff)',
              color: activeCard === 'employee_forms' ? 'white' : 'var(--theme-text, #111827)',
              padding: '1.25rem',
              borderRadius: '0.75rem',
              border: activeCard === 'employee_forms' ? 'none' : '1px solid var(--theme-border, #e5e7eb)',
              boxShadow: activeCard === 'employee_forms' ? '0 4px 6px rgba(139, 92, 246, 0.25)' : '0 1px 3px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = activeCard === 'employee_forms'
                ? '0 6px 12px rgba(139, 92, 246, 0.35)'
                : '0 4px 6px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = activeCard === 'employee_forms'
                ? '0 4px 6px rgba(139, 92, 246, 0.25)'
                : '0 1px 3px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', opacity: activeCard === 'employee_forms' ? 0.9 : 0.6, fontWeight: '600', textTransform: 'uppercase' }}>
                Employee Forms
              </div>
              <User size={20} style={{ opacity: activeCard === 'employee_forms' ? 0.8 : 0.6 }} />
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '800', lineHeight: '1' }}>
              {getEmployeeFormsCount()}
            </div>
            <div style={{ fontSize: '0.7rem', opacity: activeCard === 'employee_forms' ? 0.8 : 0.5, marginTop: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Individual forms
              {activeCard === 'employee_forms' && (
                <span style={{
                  background: 'rgba(255,255,255,0.3)',
                  padding: '0.125rem 0.375rem',
                  borderRadius: '1rem',
                  fontSize: '0.625rem',
                  fontWeight: '600'
                }}>
                  ACTIVE
                </span>
              )}
            </div>
          </div>

          {/* Organization Forms Card */}
          <div
            onClick={() => handleCardClick('establishment_forms')}
            style={{
              background: activeCard === 'establishment_forms' ? 'linear-gradient(135deg, #ec4899 0%, #d946ef 100%)' : 'var(--theme-surface, #ffffff)',
              color: activeCard === 'establishment_forms' ? 'white' : 'var(--theme-text, #111827)',
              padding: '1.25rem',
              borderRadius: '0.75rem',
              border: activeCard === 'establishment_forms' ? 'none' : '1px solid var(--theme-border, #e5e7eb)',
              boxShadow: activeCard === 'establishment_forms' ? '0 4px 6px rgba(236, 72, 153, 0.25)' : '0 1px 3px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = activeCard === 'establishment_forms'
                ? '0 6px 12px rgba(236, 72, 153, 0.35)'
                : '0 4px 6px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = activeCard === 'establishment_forms'
                ? '0 4px 6px rgba(236, 72, 153, 0.25)'
                : '0 1px 3px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', opacity: activeCard === 'establishment_forms' ? 0.9 : 0.6, fontWeight: '600', textTransform: 'uppercase' }}>
                Organization
              </div>
              <Building2 size={20} style={{ opacity: activeCard === 'establishment_forms' ? 0.8 : 0.6 }} />
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '800', lineHeight: '1' }}>
              {getOrganizationFormsCount()}
            </div>
            <div style={{ fontSize: '0.7rem', opacity: activeCard === 'establishment_forms' ? 0.8 : 0.5, marginTop: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Consolidated forms
              {activeCard === 'establishment_forms' && (
                <span style={{
                  background: 'rgba(255,255,255,0.3)',
                  padding: '0.125rem 0.375rem',
                  borderRadius: '1rem',
                  fontSize: '0.625rem',
                  fontWeight: '600'
                }}>
                  ACTIVE
                </span>
              )}
            </div>
          </div>

          {/* Statements Card */}
          <div
            onClick={() => handleCardClick('statements')}
            style={{
              background: activeCard === 'statements' ? 'linear-gradient(135deg, #14b8a6 0%, #0891b2 100%)' : 'var(--theme-surface, #ffffff)',
              color: activeCard === 'statements' ? 'white' : 'var(--theme-text, #111827)',
              padding: '1.25rem',
              borderRadius: '0.75rem',
              border: activeCard === 'statements' ? 'none' : '1px solid var(--theme-border, #e5e7eb)',
              boxShadow: activeCard === 'statements' ? '0 4px 6px rgba(20, 184, 166, 0.25)' : '0 1px 3px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = activeCard === 'statements'
                ? '0 6px 12px rgba(20, 184, 166, 0.35)'
                : '0 4px 6px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = activeCard === 'statements'
                ? '0 4px 6px rgba(20, 184, 166, 0.25)'
                : '0 1px 3px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', opacity: activeCard === 'statements' ? 0.9 : 0.6, fontWeight: '600', textTransform: 'uppercase' }}>
                Statements
              </div>
              <FileText size={20} style={{ opacity: activeCard === 'statements' ? 0.8 : 0.6 }} />
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '800', lineHeight: '1' }}>
              {getStatementsCount()}
            </div>
            <div style={{ fontSize: '0.7rem', opacity: activeCard === 'statements' ? 0.8 : 0.5, marginTop: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Bank, EPF, ESI
              {activeCard === 'statements' && (
                <span style={{
                  background: 'rgba(255,255,255,0.3)',
                  padding: '0.125rem 0.375rem',
                  borderRadius: '1rem',
                  fontSize: '0.625rem',
                  fontWeight: '600'
                }}>
                  ACTIVE
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Controls Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1.5rem',
      }}>
        <div style={{
          fontSize: '0.875rem',
          color: '#6b7280',
          fontWeight: '600',
        }}>
          {forms.length} {forms.length === 1 ? 'form' : 'forms'} found
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* View Toggle */}
          <div style={{
            display: 'flex',
            background: 'var(--theme-surface)',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',
            padding: '0.25rem',
          }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '0.5rem 0.875rem',
                background: viewMode === 'grid' ? '#667eea' : 'transparent',
                color: viewMode === 'grid' ? 'white' : '#6b7280',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.8125rem',
                fontWeight: '600',
                transition: 'all 0.2s',
              }}
            >
              <Grid3x3 size={16} />
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '0.5rem 0.875rem',
                background: viewMode === 'list' ? '#667eea' : 'transparent',
                color: viewMode === 'list' ? 'white' : '#6b7280',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.8125rem',
                fontWeight: '600',
                transition: 'all 0.2s',
              }}
            >
              <List size={16} />
              List
            </button>
          </div>

          <button
            onClick={handleRefresh}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.625rem 1.25rem',
              background: 'var(--theme-surface)',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f1f3f4';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Forms Display */}
      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '4rem',
          background: 'var(--theme-surface)',
          borderRadius: '0.75rem',
          border: '1px solid #e5e7eb',
        }}>
          <div style={{ textAlign: 'center', color: '#6b7280' }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
            <p>Loading forms...</p>
          </div>
        </div>
      ) : (
        viewMode === 'grid' ? renderGridView() : renderListView()
      )}
    </div>
  );
};

export default ViewForms;
